import React, { useRef, useEffect } from 'react';
import Tag from '@amzn/meridian/tag';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import cogTokens from '@amzn/meridian-tokens/base/icon/cog';
import starTokens from '@amzn/meridian-tokens/base/icon/star';
import trashTokens from '@amzn/meridian-tokens/base/icon/trash';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import {
  CorePropertyDetail,
  CoreModal,
  CoreConfirm,
  useToggle,
} from '@amzn/dots-core-ui';
import Expander from '@amzn/meridian/expander';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import { Configuration, useClient } from '../../api';
import { ConfigurationForm } from './ConfigurationForm';
import { ConfigurationText } from './ConfigurationText';

export type ConfigurationDetailProps = {
  configuration: Configuration;
  onDelete: () => void;
  onEdit: () => void;
  onFavorite: () => void;
  isExpanded: boolean;
  onToggleExpand: (configId: number) => void;
  fromUrlHash?: boolean;
};

export function ConfigurationDetail({
  configuration,
  onDelete,
  onEdit,
  onFavorite,
  isExpanded,
  onToggleExpand,
  fromUrlHash = false,
}: ConfigurationDetailProps): JSX.Element {
  const [isEditModalOpen, closeEditModal, openEditModal] = useToggle();
  const [isRemoveModalOpen, closeRemoveModal, openRemoveModal] = useToggle();
  const [isFavoriteModalOpen, closeFavoriteModal, openFavoriteModal] =
    useToggle();
  const { tmsClient } = useClient();
  const expanderRef = useRef<HTMLDivElement>(null);

  const handleExpanderChange = () => {
    onToggleExpand(configuration.id);
  };

  useEffect(() => {
    if (fromUrlHash && expanderRef.current) {
      expanderRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [fromUrlHash]);

  return (
    <Expander
      id={`config-detail-${configuration.id}`}
      key={configuration.name}
      open={isExpanded}
      onChange={handleExpanderChange}
      title={
        <Column ref={expanderRef} spacing="300" spacingInset="400">
          <Row widths={['grid-10', 'grid-2']}>
            <Column>
              <Row>
                <Heading
                  level={4}
                >{`${configuration.name} | ${configuration.id}`}</Heading>
                {configuration.isDefault ? (
                  <Tag type="theme">Default Config</Tag>
                ) : null}
              </Row>
              {configuration.description && (
                <Text type="b100">{configuration.description}</Text>
              )}
            </Column>
            <Row alignmentHorizontal="end" spacing="none">
              <>
                <>
                  <Button
                    type={configuration.isFavorite ? 'primary' : 'secondary'}
                    onClick={openFavoriteModal}
                  >
                    <Icon tokens={starTokens}>favorite</Icon>
                  </Button>

                  <CoreConfirm
                    title={' '}
                    isOpen={isFavoriteModalOpen}
                    onCancel={closeFavoriteModal}
                    onConfirm={async (): Promise<void> => {
                      await tmsClient.configurations.update(configuration.id, {
                        ...configuration,
                        isFavorite: !configuration.isFavorite,
                      });
                      closeFavoriteModal();
                      onFavorite();
                    }}
                  >
                    <Text alignment="center">
                      {configuration.isFavorite === true
                        ? 'Remove configuration from favorites ?'
                        : 'Add configuration to favorites ?'}
                    </Text>
                  </CoreConfirm>
                </>

                {configuration.editable && (
                  <>
                    <Button type="icon" onClick={openEditModal}>
                      <Icon tokens={cogTokens}>Settings</Icon>
                    </Button>
                    <CoreModal
                      title={`Edit ${configuration.name}`}
                      isOpen={isEditModalOpen}
                      onClose={closeEditModal}
                    >
                      <ConfigurationForm
                        configuration={configuration}
                        onCancel={closeEditModal}
                        onSubmit={async (edited): Promise<void> => {
                          await tmsClient.configurations.update(
                            configuration.id,
                            edited
                          );
                          closeEditModal();
                          onEdit();
                        }}
                      />
                    </CoreModal>
                  </>
                )}

                {configuration.deletable && (
                  <>
                    <Button type="icon" onClick={openRemoveModal}>
                      <Icon tokens={trashTokens}>Remove</Icon>
                    </Button>
                    <CoreConfirm
                      title="Remove Test Suite"
                      isOpen={isRemoveModalOpen}
                      onCancel={closeRemoveModal}
                      onConfirm={async (): Promise<void> => {
                        await tmsClient.configurations.remove(configuration.id);
                        onDelete();
                      }}
                    >
                      <Text alignment="center">
                        Are you sure you want to remove{' '}
                        <strong>{configuration.name}</strong>?
                      </Text>
                    </CoreConfirm>
                  </>
                )}
              </>
            </Row>
          </Row>
        </Column>
      }
    >
      <Row
        alignmentVertical="top"
        alignmentHorizontal="start"
        widths={['grid-6', 'grid-6']}
        spacing="300"
        spacingInset="none 300"
      >
        <ConfigurationText
          label="Configuration"
          config={configuration.config}
        />
        <ConfigurationText
          label="Merged Configuration"
          config={configuration.mergedConfig}
        />
      </Row>
      {configuration.attributes?.tags && (
        <CorePropertyDetail label="tags">
          {configuration.attributes.tags.map((t) => (
            <Tag key={`${t}-${configuration.id}`}>{t}</Tag>
          ))}
        </CorePropertyDetail>
      )}
    </Expander>
  );
}
